import { useMutation, useQuery } from '@tanstack/react-query'
import type {
  Maybe,
  Order,
  PaymentMethodQuote,
  UpdateOrderInput,
} from '@haus-storefront-react/shared-types'
import { QueryKeys } from '@haus-storefront-react/shared-types'
import { useSdk } from '@haus-storefront-react/core'

type UsePaymentMethods = {
  setOrderPaymentMethod: (
    paymentMethodCode: UpdateOrderInput['paymentMethodCode'],
  ) => Promise<Maybe<Order>>
  refetch: () => void
  paymentMethods: PaymentMethodQuote[] | undefined
  error: Error | null
  isLoading: boolean
  loadingMap: {
    paymentMethods: boolean
    setOrderPaymentMethod: boolean
  }
}

export const usePaymentMethods = (): UsePaymentMethods => {
  const sdk = useSdk()

  const {
    data: paymentMethods,
    refetch,
    error,
    isLoading,
  } = useQuery({
    queryKey: [QueryKeys.PAYMENT_METHODS, sdk],
    queryFn: () => sdk.eligiblePaymentMethods(),
  })

  const mutation = useMutation({
    mutationFn: (paymentMethodCode: UpdateOrderInput['paymentMethodCode']) =>
      sdk.updateOrder({ input: { paymentMethodCode } }),
  })

  const setOrderPaymentMethod = async (
    paymentMethodCode: UpdateOrderInput['paymentMethodCode'],
  ) => {
    return await mutation.mutateAsync(paymentMethodCode)
  }

  return {
    setOrderPaymentMethod,
    refetch,
    paymentMethods,
    error: mutation.error || error,
    isLoading: mutation.isPending || isLoading,
    loadingMap: {
      paymentMethods: isLoading,
      setOrderPaymentMethod: mutation.isPending,
    },
  }
}
